import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { getEvents } from '../../firebase';
import { getApiUrl } from '../../config';
import { User } from '../../types';
import { getEffectiveRole } from '../../utils/roleUtils';

interface EventDetailsModalProps {
  selectedEvent: any;
  user: User | null;
  onClose: () => void;
  onDelete: (eventId: string) => void;
  onUpdate: (enrichedEvents: any[]) => void;
}

export function EventDetailsModal({
  selectedEvent,
  user,
  onClose,
  onDelete,
  onUpdate
}: EventDetailsModalProps) {
  const { t, i18n } = useTranslation();
  const [editedEventData, setEditedEventData] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Check if user can edit/delete this event
  const canEditEvent = () => {
    if (!user || !selectedEvent) return false;
    const { systemRole, organizationRole } = getEffectiveRole(user);
    
    // Super admin can edit all events
    if (systemRole === 'super_admin') return true;
    
    // Check if user created this event
    const isOwner = selectedEvent.createdBy === user.id || selectedEvent.createdBy === user.email;
    
    // Department managers and program managers can edit all events in their scope
    if (organizationRole === 'department_manager' || organizationRole === 'program_manager') {
      return true;
    }
    
    // Team managers can edit events they created
    if (organizationRole === 'team_manager') {
      return isOwner;
    }
    
    // Social workers and instructors can only edit their own events
    return isOwner;
  };

  const canDeleteEvent = () => {
    if (!user || !selectedEvent) return false;
    const { systemRole, organizationRole } = getEffectiveRole(user);
    
    // Super admin can delete all events
    if (systemRole === 'super_admin') return true;
    
    // Check if user created this event
    const isOwner = selectedEvent.createdBy === user.id || selectedEvent.createdBy === user.email;
    
    // Department managers and program managers can delete all events
    if (organizationRole === 'department_manager' || organizationRole === 'program_manager') {
      return true;
    }
    
    // Team managers can delete all events in their scope
    if (organizationRole === 'team_manager') {
      return true;
    }
    
    // Social workers and instructors can only delete their own events
    return isOwner;
  };

  const handleSave = async () => {
    // Check permission before saving
    if (!canEditEvent()) {
      console.error(t('events.noPermissionToEdit') || 'You do not have permission to edit this event');
      return;
    }

    setIsSaving(true);
    try {
      // Use edited data if available, otherwise use original data
      const titleToUse = editedEventData?.title?.trim() || selectedEvent.title;
      const descriptionToUse = editedEventData?.description?.trim() || selectedEvent.description || '';
      
      if (!titleToUse) {
        return;
      }

      // Combine date and time safely
      let dateTime;
      try {
        // Use edited date/time if available, otherwise use original
        const dateStr = editedEventData?.date || (selectedEvent.date?.toDate 
          ? selectedEvent.date.toDate().toISOString().split('T')[0]
          : '');
        const timeStr = editedEventData?.time || (selectedEvent.date?.toDate 
          ? selectedEvent.date.toDate().toTimeString().slice(0, 5)
          : '');
          
        console.log('Date:', dateStr, 'Time:', timeStr);
        
        if (dateStr && timeStr) {
          // Format: YYYY-MM-DD or DD/MM/YYYY or MM/DD/YYYY
          let formattedDate = dateStr;
          let formattedTime = timeStr;
          
          // Handle DD/MM/YYYY format (convert to YYYY-MM-DD)
          if (dateStr.includes('/')) {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
              // Assume DD/MM/YYYY format
              formattedDate = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            }
          }
          
          // Ensure time format is HH:MM
          if (timeStr && !timeStr.includes(':')) {
            formattedTime = `${timeStr.substring(0, 2)}:${timeStr.substring(2, 4)}`;
          }
          
          dateTime = new Date(`${formattedDate}T${formattedTime}`);
          
          if (isNaN(dateTime.getTime())) {
            throw new Error(`Invalid date/time: ${formattedDate}T${formattedTime}`);
          }
        } else {
          // Use original date if no changes
          dateTime = selectedEvent.date?.toDate ? selectedEvent.date.toDate() : new Date();
        }
      } catch (dateError) {
        console.error('Date parsing error:', dateError);
        const errorMessage = dateError instanceof Error ? dateError.message : 'Please check the date and time format';
        console.error('Date/time error:', errorMessage);
        return;
      }
      
      // Update event in Firebase
      const eventRef = doc(db, 'events', selectedEvent.id);
      await updateDoc(eventRef, {
        title: titleToUse,
        description: descriptionToUse,
        date: dateTime,
        status: selectedEvent.status || 'active', // Keep original status
        updatedBy: user?.id || user?.email,
        updatedAt: new Date()
      });
      
      // Refresh events with enrichment to preserve patient/user names
      const eventsData = await getEvents();
      
      // Manually enrich events with patient and user data
      const enrichedEvents = await Promise.all(eventsData.map(async (event) => {
        let enrichedEvent = { ...event };
        
        // Get patient data if caseId exists
        if (event.caseId) {
          try {
            const piiResponse = await fetch(getApiUrl(`/api/patients/${event.caseId}`));
            if (piiResponse.ok) {
              const piiData = await piiResponse.json();
              enrichedEvent.patient = {
                firstName: piiData.patient.first_name,
                lastName: piiData.patient.last_name,
                email: piiData.patient.email,
                phone: piiData.patient.phone,
                address: piiData.patient.address,
                notes: piiData.patient.notes
              };
            }
          } catch (error) {
            console.log('Could not fetch patient data for caseId:', event.caseId, error);
          }
        }
        
        // Get user data for createdBy
        if (event.createdBy) {
          try {
            const userDocRef = doc(db, 'users', event.createdBy);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              const userData = userDoc.data() as any;
              enrichedEvent.createdByName = userData.name || userData.email || 'Unknown User';
            }
          } catch (error) {
            console.log('Could not fetch user data for createdBy:', event.createdBy, error);
          }
        }
        
        return enrichedEvent;
      }));
      
      onUpdate(enrichedEvents);
      onClose();
      setEditedEventData(null);
    } catch (error) {
      console.error('Error updating event:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Event update failed:', errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="event-details-modal-overlay" onClick={onClose}>
      <div className="event-details-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="event-details-modal-header">
          <h2>{t('events.editEvent')}</h2>
          <button 
            onClick={onClose}
            className="close-modal-btn"
          >
            ✕
          </button>
        </div>
        <div className="event-details-modal-body">
          {!canEditEvent() && (
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '16px',
              color: '#856404',
              fontSize: '14px'
            }}>
              🔒 {t('events.readOnlyEvent') || 'Read-only: You do not have permission to edit this event'}
            </div>
          )}
          <div className="event-detail-field">
            <label>{t('events.titleLabel')}:</label>
            <input 
              type="text"
              value={editedEventData?.title || selectedEvent.title || ''}
              onChange={(e) => setEditedEventData((prev: any) => ({ ...prev, title: e.target.value }))}
              className="event-edit-input"
              disabled={!canEditEvent()}
            />
          </div>
          <div className="event-detail-field">
            <label>{t('events.descriptionLabel')}:</label>
            <textarea 
              value={editedEventData?.description || selectedEvent.description || ''}
              onChange={(e) => setEditedEventData((prev: any) => ({ ...prev, description: e.target.value }))}
              className="event-edit-textarea"
              rows={3}
              disabled={!canEditEvent()}
            />
          </div>
          <div className="event-detail-field">
            <label>{t('events.patientLabel')}:</label>
            <div className="patient-display">
              {selectedEvent.patient 
                ? `${selectedEvent.patient.firstName} ${selectedEvent.patient.lastName}`
                : t('events.unknownPatient')
              }
            </div>
          </div>
          <div className="event-detail-field">
            <label>{t('events.dateLabel')}:</label>
            <div style={{ position: 'relative', width: '100%' }}>
              <input 
                type="date"
                value={editedEventData?.date || (selectedEvent.date?.toDate 
                  ? selectedEvent.date.toDate().toISOString().split('T')[0]
                  : '')}
                onChange={(e) => setEditedEventData((prev: any) => ({ ...prev, date: e.target.value }))}
                className="event-edit-input"
                disabled={!canEditEvent()}
                style={{
                  paddingRight: i18n.language === 'he' ? '12px' : '40px',
                  paddingLeft: i18n.language === 'he' ? '40px' : '12px',
                  cursor: canEditEvent() ? 'pointer' : 'not-allowed'
                }}
                onClick={() => {
                  if (!canEditEvent()) return;
                  const dateInput = document.querySelector('.event-edit-input[type="date"]') as HTMLInputElement;
                  if (dateInput) {
                    dateInput.showPicker();
                  }
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  ...(i18n.language === 'he' ? { left: '12px' } : { right: '12px' }),
                  pointerEvents: 'auto',
                  fontSize: '18px',
                  cursor: 'pointer',
                  zIndex: 1
                }}
                onClick={(e) => {
                  const input = (e.target as HTMLElement).parentElement?.querySelector('input[type="date"]') as HTMLInputElement;
                  if (input) {
                    input.showPicker();
                  }
                }}
                title={t('events.dateLabel')}
              >
                📅
              </span>
            </div>
          </div>
          <div className="event-detail-field">
            <label>{t('events.timeLabel')}:</label>
            <div style={{ position: 'relative', width: '100%' }}>
              <input 
                type="time"
                value={editedEventData?.time || (selectedEvent.date?.toDate 
                  ? selectedEvent.date.toDate().toTimeString().slice(0, 5)
                  : '')}
                onChange={(e) => setEditedEventData((prev: any) => ({ ...prev, time: e.target.value }))}
                className="event-edit-input"
                disabled={!canEditEvent()}
                style={{
                  paddingRight: i18n.language === 'he' ? '12px' : '40px',
                  paddingLeft: i18n.language === 'he' ? '40px' : '12px',
                  cursor: canEditEvent() ? 'pointer' : 'not-allowed'
                }}
                onClick={() => {
                  if (!canEditEvent()) return;
                  const timeInput = document.querySelector('.event-edit-input[type="time"]') as HTMLInputElement;
                  if (timeInput) {
                    timeInput.showPicker();
                  }
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  ...(i18n.language === 'he' ? { left: '12px' } : { right: '12px' }),
                  pointerEvents: 'auto',
                  fontSize: '18px',
                  cursor: 'pointer',
                  zIndex: 1
                }}
                onClick={(e) => {
                  const input = (e.target as HTMLElement).parentElement?.querySelector('input[type="time"]') as HTMLInputElement;
                  if (input) {
                    input.showPicker();
                  }
                }}
                title={t('events.timeLabel')}
              >
                🕐
              </span>
            </div>
          </div>
          <div className="event-detail-field">
            <label>{t('events.createdByLabel')}:</label>
            <div className="created-by-display">
              {selectedEvent.createdByName || t('events.unknownUser')}
            </div>
          </div>
        </div>
        <div className="event-details-modal-footer">
          {canDeleteEvent() && (
          <button 
            onClick={() => {
              if (selectedEvent) {
                onDelete(selectedEvent.id);
                onClose();
              }
            }}
            className="delete-btn"
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {t('events.deleteEvent')}
          </button>
          )}
          {canEditEvent() && (
          <button
            onClick={handleSave}
            className="save-btn"
            disabled={isSaving}
          >
            {isSaving ? t('events.saving') : t('events.saveChanges')}
          </button>
          )}
        </div>
      </div>
    </div>
  );
}

